import { Waves, Dog, Globe, CalendarDays, MapPinned, ParkingCircle, Route, Ruler, Ticket, MountainSnow, Star } from "lucide-react";
import ReportErrorButton from "./ReportErrorButton";

/** Practical visit info — one <h2> per keyword-rich question so each block can
 * rank independently in Google. Renders only fields that are populated.
 */
export default function VisitInfo({ wf }) {
  const name = wf.name;
  const items = [
    { key: "why_famous", icon: Star, h2: `Why is ${name} famous?`, value: wf.why_famous },
    { key: "swimming_allowed", icon: Waves, h2: `Can you swim at ${name}?`, value: wf.swimming_allowed },
    { key: "dog_rules", icon: Dog, h2: `Are dogs allowed at ${name}?`, value: wf.dog_rules },
    { key: "entry_fee", icon: Ticket, h2: `Is ${name} free? Do you need a permit?`, value: wf.entry_fee },
    { key: "parking_info", icon: ParkingCircle, h2: `Is there parking at ${name}?`, value: wf.parking_info },
    { key: "hike_length", icon: Ruler, h2: `How long is the hike to ${name}?`, value: wf.hike_length },
    { key: "hike_difficulty", icon: MountainSnow, h2: `${name} hike difficulty`, value: wf.hike_difficulty },
    { key: "best_time_to_visit", icon: CalendarDays, h2: `When is the best time to visit ${name}?`, value: wf.best_time_to_visit },
    { key: "trail_notes", icon: Route, h2: `Route and trail notes for ${name}`, value: wf.trail_notes },
  ].filter((it) => (it.value || "").trim());

  const nearby = (wf.nearby_attractions || []).filter(Boolean);
  const officialUrl = (wf.official_website || "").trim();

  if (items.length === 0 && nearby.length === 0 && !officialUrl) {
    return (
      <section className="rounded-2xl border border-dashed border-stone-300 bg-stone-50/60 p-5" data-testid="visit-info-pending">
        <p className="text-sm text-stone-600">
          Practical visit info (swimming, dogs, best season, nearby attractions) is being
          gathered for this waterfall. Check back shortly, or
          {" "}<a href="https://www.google.com/search" rel="noreferrer" className="underline">search the official park site</a> for the latest rules.
        </p>
      </section>
    );
  }

  return (
    <section className="rounded-2xl border border-stone-200 bg-white" data-testid="visit-info">
      <header className="px-5 py-4 border-b border-stone-100 flex items-baseline justify-between gap-3 flex-wrap">
        <h2 className="text-xl font-extrabold text-stone-900">Plan your visit to {name}</h2>
        <p className="text-[11px] text-stone-500">Please verify on the official website before you go</p>
      </header>

      <div className="divide-y divide-stone-100">
        {items.map(({ key, icon: Icon, h2, value }) => (
          <div key={key} id={`visit-${key}`} className="px-5 py-5 flex gap-3 scroll-mt-28" data-testid={`visit-info-${key}`}>
            <div className="w-9 h-9 rounded-full bg-emerald-50 text-emerald-800 flex items-center justify-center flex-shrink-0">
              <Icon className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1 flex-wrap">
                <h2 className="text-[15px] font-extrabold text-stone-900 leading-snug">{h2}</h2>
                <ReportErrorButton slug={wf.slug} field={key} fieldLabel={h2} />
              </div>
              <p className="text-sm text-stone-700 leading-relaxed mt-1.5 whitespace-pre-line">{value}</p>
            </div>
          </div>
        ))}

        {officialUrl && (
          <div id="visit-official_website" className="px-5 py-5 flex gap-3 scroll-mt-28" data-testid="visit-info-official-website">
            <div className="w-9 h-9 rounded-full bg-emerald-50 text-emerald-800 flex items-center justify-center flex-shrink-0">
              <Globe className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1 flex-wrap">
                <h2 className="text-[15px] font-extrabold text-stone-900 leading-snug">{name} official website</h2>
                <ReportErrorButton slug={wf.slug} field="official_website" fieldLabel="Official website" />
              </div>
              <a
                href={officialUrl}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="text-sm text-emerald-800 hover:underline break-all mt-1.5 block"
                data-testid="official-website-link"
              >
                {officialUrl}
              </a>
            </div>
          </div>
        )}

        {nearby.length > 0 && (
          <div id="visit-nearby_attractions" className="px-5 py-5 flex gap-3 scroll-mt-28" data-testid="visit-info-nearby_attractions">
            <div className="w-9 h-9 rounded-full bg-emerald-50 text-emerald-800 flex items-center justify-center flex-shrink-0">
              <MapPinned className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1 flex-wrap">
                <h2 className="text-[15px] font-extrabold text-stone-900 leading-snug">Attractions near {name}</h2>
                <ReportErrorButton slug={wf.slug} field="nearby_attractions" fieldLabel="Nearby attractions" />
              </div>
              <ul className="mt-2 space-y-1.5">
                {nearby.map((line, idx) => (
                  <li key={idx} className="text-sm text-stone-700 flex gap-2">
                    <span className="text-emerald-700 mt-1">•</span>
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
